import { useCallback, useState } from 'react';
import type { Brief } from '@/entities/brief';
import type { ProjectListItem } from '@/entities/project';
import { ROUTES } from '@/shared/config/routes';

/**
 * Headless state machine for the "Start Brief" flow: pick a project → fill the
 * intake form → watch generation progress → handle the created brief. It owns
 * the state and transitions but renders nothing — the consumer supplies the UI.
 *
 * The flow moves through three stages, reflected in the returned state:
 *   1. No project selected (`platformProjectId === null`) — render only the
 *      picker trigger.
 *   2. Project selected, intake open (`intakeOpen === true`) — render the
 *      intake modal.
 *   3. Payload captured, intake closed (`payload !== null`) — render the
 *      progress modal.
 */
export interface UseStartBriefFlowOptions {
  /**
   * Extra side-effect to run once the brief is created (e.g. kicking off an
   * agent review). Runs in addition to — not instead of — the built-in
   * behavior of opening the brief detail in a new tab, so the user can keep
   * working in the source surface (territories, opportunities, …). Receives the
   * chosen project id + the created brief.
   */
  onBriefCreated?: (projectId: string, brief: Brief) => void;
}

export interface UseStartBriefFlowReturn<TPayload> {
  /**
   * Doubles as the flow's "a project is selected" signal. Never falls back to the
   * internal `id`: the guard resolves scope from this path segment alone, and
   * since both are UUIDs an internal one comes back as a 403 rather than failing
   * validation — a wrong id and a real denial then look identical.
   */
  platformProjectId: string | null;
  /** Whether the intake modal should be shown. */
  intakeOpen: boolean;
  /** The payload produced by intake; null until Generate is clicked. */
  payload: TPayload | null;
  /** Call from the picker's `onProjectSelected` to open intake for that project. */
  selectProject: (project: ProjectListItem) => void;
  /** Pass to the intake modal's `onGenerate` — stores payload, switches to progress. */
  handleGenerate: (payload: TPayload) => void;
  /** Pass to both modals' `onClose` — clears all flow state. */
  reset: () => void;
  /** Pass to the progress modal's `onSuccess` — runs `onBriefCreated`, then resets. */
  handleSuccess: (brief: Brief) => void;
}

export function useStartBriefFlow<TPayload>({
  onBriefCreated,
}: UseStartBriefFlowOptions = {}): UseStartBriefFlowReturn<TPayload> {
  const [platformProjectId, setPlatformProjectId] = useState<string | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [payload, setPayload] = useState<TPayload | null>(null);

  const selectProject = useCallback((project: ProjectListItem) => {
    if (!project.platformProjectId) return;
    setPlatformProjectId(project.platformProjectId);
    setIntakeOpen(true);
  }, []);

  const handleGenerate = useCallback((next: TPayload) => {
    setPayload(next);
    setIntakeOpen(false);
  }, []);

  const reset = useCallback(() => {
    setIntakeOpen(false);
    setPayload(null);
    setPlatformProjectId(null);
  }, []);

  const handleSuccess = useCallback(
    (brief: Brief) => {
      if (platformProjectId) {
        // Run the consumer's extra side-effect first, then always open the
        // brief in a new tab so the user can keep working in the source surface.
        onBriefCreated?.(platformProjectId, brief);
        const briefUrl = `${window.location.origin}${ROUTES.PROJECT_BRIEF_DETAIL(platformProjectId, brief.id)}`;
        window.open(briefUrl, '_blank', 'noopener,noreferrer')?.focus();
      }
      reset();
    },
    [platformProjectId, onBriefCreated, reset],
  );

  return {
    platformProjectId,
    intakeOpen,
    payload,
    selectProject,
    handleGenerate,
    reset,
    handleSuccess,
  };
}
